import { RUN_POINT_SIZE_ATTR } from '@/lib/text/run-span'
import { FOREIGN_CSS_PX_TO_PT } from '@/lib/text/leading'
import type { StyleAccum } from '../types/style-accum'

/**
 * Point size from a CSS `font-size` on *foreign* HTML (a website, Word, Google Docs), where `px`
 * really is a 96dpi CSS pixel and 0.75 is the right conversion.
 *
 * This must not be used on larry's own run spans. Inside the document canvas one point is one CSS
 * px, so a larry span writes a 24pt run as `font-size: 24px` — running it through here would paste
 * it back at 18pt. App-authored spans are read from `RUN_POINT_SIZE_ATTR` instead; see
 * `parseElementPointSize`.
 */
export function parsePtFromForeignCss(value: string): number | undefined {
  if (!value) return undefined
  const ptMatch = value.match(/^([\d.]+)pt$/i)

  if (ptMatch) return parseFloat(ptMatch[1])
  const pxMatch = value.match(/^([\d.]+)px$/i)

  if (pxMatch) {
    const pt = parseFloat(pxMatch[1]) * FOREIGN_CSS_PX_TO_PT

    return pt > 0 ? pt : undefined
  }

  return undefined
}

/** A run's point size: larry's own `data-run-pointsize` when present (an in-app copy), otherwise
 *  the element's CSS font-size read as foreign 96dpi markup. */
export function parseElementPointSize(
  el: Element,
  style: CSSStyleDeclaration | undefined
): number | undefined {
  const attr = el.getAttribute(RUN_POINT_SIZE_ATTR)

  if (attr) {
    const n = parseFloat(attr)

    if (isFinite(n) && n > 0) return n
  }

  return style?.fontSize ? parsePtFromForeignCss(style.fontSize) : undefined
}

export function isBoldFontWeight(w: string): boolean {
  if (!w) return false
  if (w === 'bold' || w === 'bolder') return true
  const n = parseInt(w, 10)

  return !isNaN(n) && n >= 600
}

export function childStyleAccum(el: Element, parent: StyleAccum): StyleAccum {
  const tag = el.tagName.toUpperCase()
  const s = el instanceof HTMLElement ? el.style : null

  let { bold } = parent
  let { italic } = parent
  let { underline } = parent
  let { strikethrough } = parent
  let { letterSpacingEm } = parent
  let { lineHeight } = parent
  let { fontFamilyCssName } = parent
  let { verticalAlign } = parent

  // Semantic tags set the style
  if (tag === 'B' || tag === 'STRONG') bold = true
  if (tag === 'I' || tag === 'EM') italic = true
  if (tag === 'U') underline = true
  if (tag === 'S' || tag === 'DEL' || tag === 'STRIKE') strikethrough = true
  // WMPCM-10141 — mutually exclusive by definition (a character can't be both raised and
  // lowered), unlike underline/strikethrough above, which genuinely can combine.
  if (tag === 'SUP') verticalAlign = 'super'
  if (tag === 'SUB') verticalAlign = 'sub'

  // Inline styles override semantic tags
  if (s?.fontWeight) bold = isBoldFontWeight(s.fontWeight)
  if (s?.fontStyle) italic = s.fontStyle === 'italic' || s.fontStyle === 'oblique'
  // Word/Google Docs commonly express superscript/subscript as inline CSS rather than (or
  // alongside) a semantic tag — inline styles win, same precedence as bold/italic above.
  // Any other value (`baseline`, `top`, `middle`, ...) clears it, matching how `td === 'none'`
  // clears underline/strikethrough below.
  if (s?.verticalAlign) {
    verticalAlign =
      s.verticalAlign === 'super' ? 'super' : s.verticalAlign === 'sub' ? 'sub' : undefined
  }

  // Browsers copying from a web page often serialize only the longhand (`text-decoration-line:
  // line-through`), which does not always surface through the `textDecoration` shorthand.
  const td = [s?.textDecoration, s?.textDecorationLine].filter(Boolean).join(' ')

  if (td) {
    if (td.split(/\s+/).every((part) => part === 'none')) {
      underline = false
      strikethrough = false
    } else {
      if (td.includes('underline')) underline = true
      if (td.includes('line-through')) strikethrough = true
    }
  }

  const pointSize = parseElementPointSize(el, s ?? undefined) ?? parent.pointSize

  // letter-spacing encodes tracking + kerning combined as `${value}em`
  if (s?.letterSpacing && s.letterSpacing !== 'normal') {
    const emMatch = s.letterSpacing.match(/^(-?[\d.]+)em$/)

    if (emMatch) letterSpacingEm = parseFloat(emMatch[1])
  }

  if (s?.lineHeight && s.lineHeight !== 'normal') {
    ;({ lineHeight } = s)
  }

  if (s?.fontFamily) {
    // Keep the WHOLE stack, not just the first entry (WMPCM-9199). Word and Google Docs emit
    // `font-family: Calibri, sans-serif` and often a longer fallback list; taking only the head
    // meant that when the head was not a permissible family the run silently inherited the caret's
    // font, even if a later entry in the same stack WAS available. `resolveRun` walks the list in
    // order, so declared preference still wins.
    fontFamilyCssName = s.fontFamily.replace(/["']/g, '').trim() || undefined
  }

  return {
    bold,
    italic,
    underline,
    strikethrough,
    pointSize,
    letterSpacingEm,
    lineHeight,
    fontFamilyCssName,
    verticalAlign,
  }
}
